<script setup lang="ts">
import { computed } from 'vue';
import { DismissIcon } from '@blpde/nx-icons';
import { hueVars, type NxHue } from '../lib/hue';

const props = withDefaults(
  defineProps<{
    /** Highlighted / toggled-on state. */
    selected?: boolean;
    disabled?: boolean;
    /** Optional leading status dot color. */
    dotColor?: string;
    /** Render as a non-interactive pill (`span`). */
    static?: boolean;
    /**
     * Render as one listbox option (`role="option"`): a single span, no tab
     * stop and nothing focusable inside. `mousedown` is cancelled so the
     * option never takes the focus from the field that owns it. `selected`
     * is the active option (the one `aria-activedescendant` names) and draws
     * the focus ring, not the selected fill. A removable option's × is
     * mouse-only; removing it from the keyboard is the listbox owner's job.
     * Wins over `static`.
     */
    option?: boolean;
    /** Defaults to 's' */
    size?: 'xs' | 's' | 'm' | 'l' | 'xl';
    /** Trailing × that emits `remove`. Ignored in `static` mode (`option` wins over `static`). */
    removable?: boolean;
    /** Accessible label for the × button. */
    removeLabel?: string;
    /** Category hue: tinted fill, border and text (e.g. AI suggestion chips). */
    hue?: NxHue;
    /** Counter drawn after the label (a number, or a text such as a dash). `null` = none. */
    count?: number | string | null;
  }>(),
  {
    selected: false,
    disabled: false,
    dotColor: undefined,
    static: false,
    option: false,
    size: 's',
    removable: false,
    removeLabel: 'Remove',
    hue: undefined,
    count: null,
  }
);

const hueStyle = computed(() => hueVars(props.hue));

/** Spoken between label and counter (visually hidden): "Open, 732". */
const COUNT_SEPARATOR = ', ';

/** `0` counts; only `null`, `undefined` and the empty string mean "no counter". */
const hasCount = computed(() => props.count != null && props.count !== '');

const emit = defineEmits<{ click: [event: MouseEvent]; remove: [event: MouseEvent] }>();

/** The two-button ×. An option draws its own span; `static` has neither. */
const showRemove = computed(() => props.removable && !props.static && !props.option);

function onClick(event: MouseEvent) {
  if (props.disabled) return;
  // `option` wins over `static`: an option still reports the click.
  if (props.static && !props.option) return;
  emit('click', event);
}

function onRemove(event: MouseEvent) {
  if (props.disabled) return;
  // Keep remove independent from the chip's own click. `dblclick` is stopped in the
  // template for the same reason: a consumer that binds `@dblclick` on the chip (the
  // B-DMS attachment tiles open the preview that way) would otherwise both remove
  // the item and act on it when the × is double-clicked.
  event.stopPropagation();
  emit('remove', event);
}
</script>

<template>
  <!-- Option: one span. Nothing inside can take the focus; the field that owns
       the listbox names this span through aria-activedescendant. -->
  <span
    v-if="option"
    class="nx-chip option"
    :class="[
      `height-${props.size}`,
      { disabled, removable, 'has-hue': !!props.hue, 'is-active': selected },
    ]"
    :style="hueStyle"
    role="option"
    :aria-selected="selected ? 'true' : 'false'"
    :aria-disabled="disabled ? 'true' : undefined"
    @mousedown.prevent
    @click="onClick"
  >
    <span v-if="dotColor" class="nx-chip-dot" :style="{ background: dotColor }" />
    <slot />
    <span v-if="hasCount" class="nx-chip__count-sep">{{ COUNT_SEPARATOR }}</span>
    <span v-if="hasCount" class="nx-chip__count">{{ count }}</span>
    <span
      v-if="removable"
      class="nx-chip__remove"
      aria-hidden="true"
      :title="removeLabel"
      @click="onRemove"
      @dblclick.stop
      @mousedown.prevent
    >
      <DismissIcon class="nxt-icon-md" />
    </span>
  </span>

  <!-- Removable variant: a button cannot nest a button, so the pill becomes a
       span container holding a label button and a separate × button. -->
  <span
    v-else-if="showRemove"
    class="nx-chip removable"
    :class="[`height-${props.size}`, { selected, disabled, 'has-hue': !!props.hue }]"
    :style="hueStyle"
  >
    <button class="nx-chip__label" type="button" :disabled="disabled" @click="onClick">
      <span v-if="dotColor" class="nx-chip-dot" :style="{ background: dotColor }" />
      <slot />
      <span v-if="hasCount" class="nx-chip__count-sep">{{ COUNT_SEPARATOR }}</span>
      <span v-if="hasCount" class="nx-chip__count">{{ count }}</span>
    </button>
    <button
      class="nx-chip__remove"
      type="button"
      :disabled="disabled"
      :aria-label="removeLabel"
      :title="removeLabel"
      @click="onRemove"
      @dblclick.stop
    >
      <DismissIcon class="nxt-icon-md" />
    </button>
  </span>

  <!-- Default: unchanged single button / static span pill. -->
  <component
    :is="static ? 'span' : 'button'"
    v-else
    class="nx-chip"
    :class="[`height-${props.size}`, { selected, static, 'has-hue': !!props.hue }]"
    :style="hueStyle"
    :type="static ? undefined : 'button'"
    :disabled="static ? undefined : disabled"
    @click="onClick"
  >
    <span v-if="dotColor" class="nx-chip-dot" :style="{ background: dotColor }" />
    <slot />
    <span v-if="hasCount" class="nx-chip__count-sep">{{ COUNT_SEPARATOR }}</span>
    <span v-if="hasCount" class="nx-chip__count">{{ count }}</span>
  </component>
</template>

<style scoped>
/* NxChip consumes the --nxt-chip-* component tokens (I-596), mirroring the
   NxButton/NxBadge pilots: the base rule reads only the component's own
   tokens, and each state (hover/selected/disabled) re-points bg/fg/border. */
.nx-chip {
  display: inline-flex;
  align-items: center;
  gap: var(--nxt-space-xs);
  background: var(--nxt-chip-bg);
  border: var(--nxt-hairline) solid var(--nxt-chip-border);
  border-radius: var(--nxt-radius-full);
  padding: var(--nxt-space-2xs) var(--nxt-space-xs);
  font-weight: var(--nxt-font-weight-medium);
  font-size: var(--nxt-chip-size);
  color: var(--nxt-chip-fg);
  cursor: pointer;
  transition:
    background-color 0.12s,
    border-color 0.12s,
    color 0.12s;
}

.nx-chip:hover:not(:disabled):not(.static) {
  background: var(--nxt-chip-bg-hover);
}

.nx-chip.selected {
  background: var(--nxt-chip-bg-selected);
  border-color: var(--nxt-chip-border-selected);
  color: var(--nxt-chip-fg-selected);
}

/* Category hue: tinted like the badge's soft emphasis; selected fills solid. */
.nx-chip.has-hue {
  --nxt-chip-bg: var(--hue-subtle-bg);
  --nxt-chip-bg-hover: var(--hue-subtle-bg);
  --nxt-chip-border: var(--hue-border);
  --nxt-chip-fg: var(--hue-fg);
  --nxt-chip-bg-selected: var(--hue);
  --nxt-chip-border-selected: var(--hue);
  --nxt-chip-fg-selected: var(--hue-on);
}
.nx-chip.has-hue:hover:not(:disabled):not(.static) {
  border-color: var(--hue);
}

.nx-chip:disabled {
  cursor: not-allowed;
  background: var(--nxt-chip-bg-disabled);
  border-color: var(--nxt-chip-bg-disabled);
  color: var(--nxt-disabled-fg);
}

.nx-chip.static {
  cursor: default;
}

.nx-chip-dot {
  width: 0.5em;
  height: 0.5em;
  border-radius: var(--nxt-radius-full);
  flex: 0 0 auto;
}

/* Counter ------------------------------------------------------------------ */
/* Round brand pill after the label, no border. It is part of the chip's accessible
   name: the visually hidden ", " reads "Open, 732". On a selected chip the colours
   invert so the counter stays readable on the brand tint. */
.nx-chip__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  min-width: 1.5em;
  padding-inline: 0.4em;
  border-radius: var(--nxt-radius-full);
  background: var(--nxt-brand-subtle-bg);
  color: var(--nxt-brand-bg);
  font-size: 0.85em;
  line-height: 1.5;
  font-variant-numeric: tabular-nums;
}

.nx-chip__count-sep {
  position: absolute;
  width: var(--nxt-hairline);
  height: var(--nxt-hairline);
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}

.nx-chip.selected .nx-chip__count {
  background: var(--nxt-surface-bg);
  color: var(--nxt-brand-bg);
}

.nx-chip.has-hue .nx-chip__count {
  background: var(--hue);
  color: var(--hue-on);
}
.nx-chip.has-hue.selected .nx-chip__count {
  background: var(--hue-on);
  color: var(--hue);
}

.nx-chip.nx-chip:disabled .nx-chip__count,
.nx-chip.removable.disabled .nx-chip__count {
  background: transparent;
  color: var(--nxt-disabled-fg);
}

@media (forced-colors: active) {
  .nx-chip__count {
    border: var(--nxt-hairline) solid currentColor;
    background: transparent;
    color: inherit;
  }
}

/* Removable variant ------------------------------------------------------- */
/* The container span is not the click target; the inner buttons are. Its own
   :disabled pseudo never applies (span), so gate on the `.disabled` class. */
.nx-chip.removable {
  cursor: default;
  padding-inline-end: var(--nxt-space-2xs);
}

.nx-chip.removable:hover:not(.disabled) {
  background: var(--nxt-chip-bg-hover);
}

.nx-chip.removable.disabled {
  background: var(--nxt-chip-bg-disabled);
  border-color: var(--nxt-chip-bg-disabled);
  color: var(--nxt-disabled-fg);
}

.nx-chip__label {
  display: inline-flex;
  align-items: center;
  gap: var(--nxt-space-xs);
  background: transparent;
  border: none;
  padding: 0;
  font: inherit;
  color: inherit;
  cursor: pointer;
}

.nx-chip.removable.disabled .nx-chip__label {
  cursor: not-allowed;
}

.nx-chip__remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  padding: var(--nxt-space-2xs);
  margin: 0;
  color: var(--nxt-chip-fg);
  border-radius: var(--nxt-radius-full);
  cursor: pointer;
  transition:
    background-color 0.12s,
    color 0.12s;
}

.nx-chip__remove:hover:not(:disabled) {
  color: var(--nxt-danger-bg);
  background: var(--nxt-danger-subtle-bg);
}

.nx-chip__remove:active:not(:disabled) {
  color: var(--nxt-danger-bg);
  background: var(--nxt-danger-subtle-bg-hover);
}

.nx-chip.removable.disabled .nx-chip__remove {
  cursor: not-allowed;
}

/* Option ------------------------------------------------------------------- */
/* One span, so the field that owns the listbox keeps the focus. `selected` is
   the active option: the focus ring, not the selected fill. The × is the same
   control as the button variant, without a tab stop — the listbox owner removes
   from the keyboard. */
.nx-chip.option {
  cursor: pointer;
}

.nx-chip.option.is-active {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-focus-ring);
  outline-offset: var(--nxt-hairline);
}

@media (forced-colors: active) {
  .nx-chip.option.is-active {
    outline-color: Highlight;
  }
}

.nx-chip.option.disabled,
.nx-chip.option.disabled:hover,
.nx-chip.option.disabled.has-hue:hover {
  cursor: not-allowed;
  background: var(--nxt-chip-bg-disabled);
  border-color: var(--nxt-chip-bg-disabled);
  color: var(--nxt-disabled-fg);
}

/* The × is a span, so `:disabled` never matches it and the danger hover would. */
.nx-chip.option.disabled .nx-chip__remove:hover,
.nx-chip.option.disabled .nx-chip__remove:active {
  cursor: not-allowed;
  color: var(--nxt-chip-fg);
  background: transparent;
}

.nx-chip.option.disabled .nx-chip__count {
  background: transparent;
  color: var(--nxt-disabled-fg);
}

.nx-chip.height-xs {
  --nxt-chip-size: var(--nxt-font-size-xs);
}
.nx-chip.height-s {
  --nxt-chip-size: var(--nxt-font-size-sm);
}
.nx-chip.height-m {
  --nxt-chip-size: var(--nxt-font-size-md);
}
.nx-chip.height-l {
  --nxt-chip-size: var(--nxt-font-size-lg);
}
.nx-chip.height-xl {
  --nxt-chip-size: var(--nxt-font-size-xl);
}
</style>
